import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Bus, Walkaround, api } from "../../../logic/api";
import EditableGroup, { EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import EditableField, { EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";
import { CharacterCasing } from "../../../components/editable/EditableField/fields/TextInputField";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { SessionContext } from "../../../utility/useSession";
import { IDropDownListItem } from "../../../components/editable/EditableField/fields/DropDownField";
import { useTranslation } from "react-i18next";

export interface IBusEdit {
    show: boolean;
    onClose: (didUpdate: boolean, bus: Bus | null) => void;
    bus: Bus | null;
}
export const BusEdit: React.FC<IBusEdit> = (props) => {
    const save = api.hooks.bus.save();
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const [bus, setBus] = useState<Bus | null>(props.bus == null ? null : { ...props.bus });

    const [wasBusVOR, setWasBusVOR] = useState<boolean>(props.bus?.isVOR ?? false);

    const [showVORReason, setShowVORReason] = useState<boolean>(false);

    const getWalkarounds = api.hooks.walkaround.listUnpaginated();

    useEffect(() => {
        getWalkarounds.send(session.current.context, {});
    }, []);

    useEffect(() => {
        setBus(props.bus == null ? null : { ...props.bus });
        setWasBusVOR(props.bus?.isVOR ?? false);
        setShowVORReason(false);
    }, [props.bus]);

    const updateBus = async (wa: Bus) => {
        setBus(wa);
        return true;
    };

    if (bus == null) return <Fragment />;

    return <Dialog open={props.show}>
        <DialogTitle>{t("walkarounds.editBus")}</DialogTitle>
        <DialogContent>
            <EditableGroup<Bus>
                id="Bus"
                testId="bus-edit-group"
                editType={EditableGroupType.alwaysEditing}
                data={bus}
                onDataChanged={(data) => updateBus(data)}
            >
                <EditableField<Bus, string>
                    inputType={EditableFieldTypes.text}
                    label={t("walkarounds.registration")}
                    required={true}
                    maxLength={10}
                    useFullWidthWhenEditing={true}
                    casing={CharacterCasing.upper}
                    onProvide={(data) => data.reg ?? ""}
                    onChange={(data, value) => data.reg = value}
                />
                <EditableField<Bus, IDropDownListItem>
                    inputType={EditableFieldTypes.enumDropDownField}
                    useFullWidthWhenEditing={true}
                    label={t("walkarounds.distanceUnit")}
                    onProvide={(data) => {
                        const v = data.distanceUnit == "km" ? "km" : "miles";
                        return {
                            name: v,
                            value: v,
                        };
                    }}
                    onChange={(data, value) => {
                        setBus({ ...bus, distanceUnit: value.value || "miles" });
                    }}
                    onRequestData={async () => {
                        return [
                            { name: "miles", value: "miles" },
                            { name: "km", value: "km" }
                        ];
                    }}
                />
                <EditableField<Bus, IDropDownListItem>
                    inputType={EditableFieldTypes.enumDropDownField}
                    useFullWidthWhenEditing={true}
                    label={t("walkarounds.walkaround")}

                    onProvide={() => {
                        return {
                            name: bus.walkaround?.abbrev ?? "",
                            value: `${bus.walkaround?.id ?? ""}`,
                        };
                    }}
                    onRequestText={(data) => {
                        return data.walkaround?.abbrev ?? "";
                    }}
                    onChange={(data, value) => {
                        if (value.value == "") {
                            setBus({ ...bus, walkaround: undefined });
                            return;
                        }
                        const wa = {
                            ...bus.walkaround,
                            id: value.value,
                            abbrev: value.name,
                        } as Walkaround;

                        setBus({ ...bus, walkaround: wa });
                    }}
                    onRequestData={async () => {
                        const items: IDropDownListItem[] = [];
                        items.push({
                            name: t("walkarounds.unassigned"),
                            value: "",
                        });
                        for (const w of getWalkarounds.data ?? []) {
                            items.push({
                                name: w?.abbrev ?? "",
                                value: `${w?.id ?? ""}`,
                            });
                        }
                        return items;
                    }}
                />

                <EditableField<Bus, IDropDownListItem>
                    inputType={EditableFieldTypes.enumDropDownField}
                    useFullWidthWhenEditing={true}
                    label={t("booking.cards.status")}
                    onProvide={(data) => {
                        return {
                            name: data.isVOR ? t("walkarounds.vor") : t("walkarounds.inService"),
                            value: data.isVOR ? "1" : "0",
                        };
                    }}
                    onChange={(data, value) => {
                        if (wasBusVOR == false && (bus.isVOR == false && value.value == "1")) {
                            setShowVORReason(true);
                        }
                        setBus({ ...bus, isVOR: value.value == "1" });
                    }}
                    onRequestData={async () => {
                        return [
                            { name: t("walkarounds.inService"), value: "0" },
                            { name: t("walkarounds.vor"), value: "1" }
                        ];
                    }}
                />

                {(showVORReason) && (
                    <EditableField<Bus, string>
                        inputType={EditableFieldTypes.textMultiLineFull}
                        label={t("walkarounds.vorReason")}
                        required={true}
                        maxLength={1000}

                        useFullWidthWhenEditing={true}

                        onProvide={(data) => data.vorReason ?? ""}
                        onChange={(data, value) => data.vorReason = value}
                    />
                )}
            </EditableGroup>
            <ProgressDialog status={save.status}></ProgressDialog>
            <ErrorDialog err={save.err}></ErrorDialog>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => {
                props.onClose(false, null);
            }}>{t("general.close")}</Button>
            <Button onClick={() => {
                save.send(session.current.context, bus, (w) => {
                    if (w == null) return;
                    props.onClose(true, bus);
                });

            }}>{t("general.save")}</Button>
        </DialogActions>
    </Dialog>
};
